/**
 * AppLayout — persistent navigation shell (Pattern 8.9 / NFR-A11Y-06).
 *
 * Provides:
 *   - Skip navigation link (visually hidden, visible on keyboard focus)
 *   - Top navigation bar with NavLinks and unacknowledged alert badge
 *   - <main> landmark with id="main-content" as skip link target
 *
 * Accessibility (NFR-A11Y-01, NFR-A11Y-06):
 *   - Skip link allows keyboard users to jump past the nav bar
 *   - NavLink provides aria-current="page" on the active route
 *   - Nav badge shows count of unacknowledged alerts (BR-U5-29)
 */
import { NavLink } from 'react-router-dom';
import { useAlerts } from '@/api/alertsApi';
import { usePollingInterval } from '@/hooks/usePollingInterval';
import type { ReactNode } from 'react';
import styles from './AppLayout.module.css';

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const [pollingInterval] = usePollingInterval();
  const { data: alerts } = useAlerts(pollingInterval);

  const unacknowledgedCount = alerts?.filter((a) => !a.acknowledged).length ?? 0;

  return (
    <>
      {/* Skip navigation link — Pattern 8.9 */}
      <a href="#main-content" className={styles.skipNav}>
        Skip to main content
      </a>

      <nav className={styles.nav} aria-label="Main navigation">
        <div className={styles.navInner}>
          <span className={styles.brand}>System Monitor</span>
          <ul className={styles.navLinks} role="list">
            <li>
              <NavLink
                to="/"
                end
                className={({ isActive }) =>
                  isActive ? `${styles.navLink} ${styles.active}` : styles.navLink
                }
              >
                Dashboard
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/accounts"
                className={({ isActive }) =>
                  isActive ? `${styles.navLink} ${styles.active}` : styles.navLink
                }
              >
                Accounts
              </NavLink>
            </li>
          </ul>
          {unacknowledgedCount > 0 && (
            <span
              className={styles.alertBadge}
              aria-label={`${unacknowledgedCount} unacknowledged alert${unacknowledgedCount !== 1 ? 's' : ''}`}
              title={`${unacknowledgedCount} unacknowledged alert${unacknowledgedCount !== 1 ? 's' : ''}`}
            >
              {unacknowledgedCount > 99 ? '99+' : unacknowledgedCount}
            </span>
          )}
        </div>
      </nav>

      <main id="main-content" tabIndex={-1} className={styles.main}>
        {children}
      </main>
    </>
  );
}
